/**
 * AxLabs Mathematics Experiments - Statistics
 *
 * Collection of experiments exploring statistical concepts and analyses.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// MEAN VS MEDIAN SENSITIVITY
// ============================================================================
export const MeanVsMedianSensitivity = createExperimentFromSchema({
    id: 'mean-vs-median-sensitivity',
    name: 'Mean vs Median Sensitivity',
    domain: 'mathematics',
    description: 'Compare how mean and median respond to outliers',
    conceptTags: ['Mean', 'Median', 'Outliers', 'Central Tendency', 'Robustness'],
    difficulty: 2,
    stateVariables: [
        { id: 'outlier', name: 'Outlier Value', unit: 'dimensionless', initial: 0, symbol: 'x_{out}' },
        { id: 'mean', name: 'Mean', unit: 'dimensionless', initial: 5, symbol: '\\bar{x}' },
        { id: 'median', name: 'Median', unit: 'dimensionless', initial: 5, symbol: '\\tilde{x}' },
        { id: 'meanChange', name: 'Mean Δ', unit: 'dimensionless', initial: 0, symbol: '\\Delta\\bar{x}' },
        { id: 'medianChange', name: 'Median Δ', unit: 'dimensionless', initial: 0, symbol: '\\Delta\\tilde{x}' },
    ],
    parameters: [
        { id: 'baseData', name: 'Base Data Set', unit: 'dimensionless', min: 0, max: 2, default: 1, symbol: 'D' },
        { id: 'outlierRate', name: 'Outlier Rate', unit: 'dimensionless/s', min: -10, max: 10, default: 1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'mean-formula',
            name: 'Mean Formula',
            latex: '\\bar{x} = \\frac{1}{n}\\sum_{i=1}^n x_i',
            description: 'Arithmetic mean is sensitive to outliers',
        },
        {
            id: 'median-formula',
            name: 'Median Formula',
            latex: '\\tilde{x} = \\text{middle value when sorted}',
            description: 'Median is robust to outliers',
        },
    ],
    derivatives: (state, params) => {
        const baseData = Math.round(getParam(params, 'baseData'));
        const outlierRate = getParam(params, 'outlierRate');
        const outlier = getState(state, 'outlier');
        const nextOutlier = outlier + outlierRate * 0.016;
        // Define base data sets
        const baseSets = [
            [1, 2, 3, 4, 5, 6, 7, 8, 9],
            [4, 4, 4, 5, 5, 5, 6, 6, 6],
            [0, 1, 2, 5, 8, 9, 10],
        ];
        const base = baseSets[baseData] ?? baseSets[0];
        const data = [...(base ?? []), nextOutlier].sort((a, b) => a - b);
        const sum = data.reduce((a, b) => a + b, 0);
        const mean = sum / data.length;
        const mid = Math.floor(data.length / 2);
        const median = data.length % 2 === 0
            ? ((data[mid - 1] ?? 0) + (data[mid] ?? 0)) / 2
            : (data[mid] ?? 0);
        const baseSum = (base ?? []).reduce((a, b) => a + b, 0);
        const baseMean = baseSum / (base?.length ?? 1);
        const baseMid = Math.floor((base?.length ?? 1) / 2);
        const baseMedian = (base?.length ?? 0) % 2 === 0
            ? (((base ?? [])[baseMid - 1] ?? 0) + ((base ?? [])[baseMid] ?? 0)) / 2
            : ((base ?? [])[baseMid] ?? 0);
        return {
            outlier: nextOutlier - outlier,
            mean: mean - getState(state, 'mean'),
            median: median - getState(state, 'median'),
            meanChange: (mean - baseMean) - getState(state, 'meanChange'),
            medianChange: (median - baseMedian) - getState(state, 'medianChange'),
        };
    },
    bounds: {
        outlier: { min: -100, max: 100 },
        mean: { min: -20, max: 30 },
        median: { min: 0, max: 10 },
    },
});
// ============================================================================
// STANDARD DEVIATION EFFECTS
// ============================================================================
export const StandardDeviationEffects = createExperimentFromSchema({
    id: 'standard-deviation-effects',
    name: 'Standard Deviation Effects',
    domain: 'mathematics',
    description: 'Explore how spread affects standard deviation',
    conceptTags: ['Standard Deviation', 'Variance', 'Spread', 'Bell Curve'],
    difficulty: 3,
    stateVariables: [
        { id: 'spread', name: 'Spread Factor', unit: 'dimensionless', initial: 1, symbol: 's' },
        { id: 'mean', name: 'Mean', unit: 'dimensionless', initial: 0, symbol: '\\mu' },
        { id: 'stdev', name: 'Std Dev σ', unit: 'dimensionless', initial: 1, symbol: '\\sigma' },
        { id: 'variance', name: 'Variance σ²', unit: 'dimensionless', initial: 1, symbol: '\\sigma^2' },
        { id: 'within1', name: 'Within 1σ', unit: 'dimensionless', initial: 68.27, symbol: 'P(\\mu\\pm\\sigma)' },
        { id: 'within2', name: 'Within 2σ', unit: 'dimensionless', initial: 95.45, symbol: 'P(\\mu\\pm2\\sigma)' },
    ],
    parameters: [
        { id: 'spreadTarget', name: 'Target Spread', unit: 'dimensionless', min: 0.1, max: 5, default: 1, symbol: 's' },
        { id: 'rate', name: 'Rate of Change', unit: 'dimensionless/s', min: -1, max: 1, default: 0.1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'stdev-formula',
            name: 'Standard Deviation',
            latex: '\\sigma = \\sqrt{\\frac{1}{n}\\sum_{i=1}^n (x_i - \\mu)^2}',
            description: 'Measure of spread from the mean',
        },
        {
            id: 'empirical-rule',
            name: 'Empirical Rule (68-95-99.7)',
            latex: 'P(\\mu-\\sigma < X < \\mu+\\sigma) \\approx 68.27\\%',
            description: 'For normal distribution, percentage within standard deviations',
        },
    ],
    derivatives: (state, params) => {
        void getParam(params, 'spreadTarget'); // Used for UI
        const rate = getParam(params, 'rate');
        const spread = getState(state, 'spread');
        const nextSpread = Math.max(0.1, spread + rate * 0.016);
        // Generate normal-like data with given spread
        const baseData = [-3, -2, -1, 0, 1, 2, 3];
        const data = baseData.map(x => x * nextSpread);
        const mean = data.reduce((a, b) => a + b, 0) / data.length;
        const variance = data.reduce((a, b) => a + (b - mean) ** 2, 0) / data.length;
        const stdev = Math.sqrt(variance);
        return {
            spread: nextSpread - spread,
            mean: mean - getState(state, 'mean'),
            stdev: stdev - getState(state, 'stdev'),
            variance: variance - getState(state, 'variance'),
            within1: 0,
            within2: 0,
        };
    },
    bounds: {
        spread: { min: 0.1, max: 10 },
        stdev: { min: 0, max: 30 },
        variance: { min: 0, max: 900 },
    },
});
// ============================================================================
// CORRELATION COEFFICIENT BEHAVIOR
// ============================================================================
export const CorrelationCoefficientBehavior = createExperimentFromSchema({
    id: 'correlation-coefficient-behavior',
    name: 'Correlation Coefficient Behavior',
    domain: 'mathematics',
    description: 'Explore correlation coefficient r between variables',
    conceptTags: ['Correlation', 'Covariance', 'Linear Relationship', 'Scatter Plot'],
    difficulty: 4,
    stateVariables: [
        { id: 'slope', name: 'Slope Factor', unit: 'dimensionless', initial: 1, symbol: 'm' },
        { id: 'noise', name: 'Noise Level', unit: 'dimensionless', initial: 0, symbol: '\\epsilon' },
        { id: 'r', name: 'Correlation r', unit: 'dimensionless', initial: 1, symbol: 'r' },
        { id: 'r2', name: 'R²', unit: 'dimensionless', initial: 1, symbol: 'R^2' },
        { id: 'covariance', name: 'Covariance', unit: 'dimensionless', initial: 1, symbol: 'Cov(X,Y)' },
    ],
    parameters: [
        { id: 'targetSlope', name: 'Target Slope', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'm' },
        { id: 'noiseLevel', name: 'Noise Level', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: '\\sigma' },
        { id: 'rate', name: 'Change Rate', unit: 'dimensionless/s', min: -0.5, max: 0.5, default: 0.05, symbol: 'r' },
    ],
    equations: [
        {
            id: 'correlation-formula',
            name: 'Pearson Correlation',
            latex: 'r = \\frac{\\sum(x_i-\\bar{x})(y_i-\\bar{y})}{\\sqrt{\\sum(x_i-\\bar{x})^2\\sum(y_i-\\bar{y})^2}}',
            description: 'Measures linear relationship strength',
        },
        {
            id: 'r-squared',
            name: 'R-squared',
            latex: 'R^2 = r^2',
            description: 'Proportion of variance explained',
        },
    ],
    derivatives: (state, params) => {
        void getParam(params, 'targetSlope'); // Used for UI
        void getParam(params, 'noiseLevel'); // Used for UI
        const rate = getParam(params, 'rate');
        const slope = getState(state, 'slope');
        const noise = getState(state, 'noise');
        const nextSlope = slope + rate * 0.016;
        const nextNoise = Math.max(0, Math.min(2, noise + rate * 0.02));
        // Generate data points
        const n = 50;
        const x = [];
        const y = [];
        for (let i = 0; i < n; i++) {
            const xi = (i - n / 2) / 5;
            const seed = i * 1.618;
            const yi = nextSlope * xi + (Math.sin(seed) * nextNoise);
            x.push(xi);
            y.push(yi);
        }
        // Calculate correlation
        const meanX = x.reduce((a, b) => a + b, 0) / n;
        const meanY = y.reduce((a, b) => a + b, 0) / n;
        let num = 0;
        let denX = 0;
        let denY = 0;
        for (let i = 0; i < n; i++) {
            const dx = (x[i] ?? 0) - meanX;
            const dy = (y[i] ?? 0) - meanY;
            num += dx * dy;
            denX += dx * dx;
            denY += dy * dy;
        }
        const r = (denX > 0 && denY > 0) ? num / Math.sqrt(denX * denY) : 0;
        const r2 = r * r;
        const covariance = num / n;
        return {
            slope: nextSlope - slope,
            noise: nextNoise - noise,
            r: r - getState(state, 'r'),
            r2: r2 - getState(state, 'r2'),
            covariance: covariance - getState(state, 'covariance'),
        };
    },
    bounds: {
        slope: { min: -5, max: 5 },
        noise: { min: 0, max: 3 },
        r: { min: -1, max: 1 },
        r2: { min: 0, max: 1 },
    },
});
// ============================================================================
// REGRESSION LINE FITTING
// ============================================================================
export const RegressionLineFitting = createExperimentFromSchema({
    id: 'regression-line-fitting',
    name: 'Regression Line Fitting',
    domain: 'mathematics',
    description: 'Visualize least squares regression line fitting',
    conceptTags: ['Linear Regression', 'Least Squares', 'Residuals', 'Line of Best Fit'],
    difficulty: 4,
    stateVariables: [
        { id: 'x', name: 'X Position', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'yActual', name: 'Actual Y', unit: 'dimensionless', initial: 2, symbol: 'y' },
        { id: 'yPredicted', name: 'Predicted ŷ', unit: 'dimensionless', initial: 2, symbol: '\\hat{y}' },
        { id: 'residual', name: 'Residual', unit: 'dimensionless', initial: 0, symbol: 'e = y - \\hat{y}' },
        { id: 'slope', name: 'Slope m', unit: 'dimensionless', initial: 1, symbol: 'm' },
        { id: 'intercept', name: 'Intercept b', unit: 'dimensionless', initial: 2, symbol: 'b' },
        { id: 'sse', name: 'Sum of Squared Errors', unit: 'dimensionless', initial: 0, symbol: 'SSE' },
    ],
    parameters: [
        { id: 'noise', name: 'Data Noise', unit: 'dimensionless', min: 0, max: 3, default: 0.5, symbol: '\\sigma' },
        { id: 'trueSlope', name: 'True Slope', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'm_{true}' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.1, default: 0.02, symbol: 'v' },
    ],
    equations: [
        {
            id: 'regression-line',
            name: 'Regression Line',
            latex: '\\hat{y} = mx + b',
            description: 'Line of best fit',
        },
        {
            id: 'least-squares',
            name: 'Least Squares Formulas',
            latex: 'm = \\frac{n\\sum xy - \\sum x\\sum y}{n\\sum x^2 - (\\sum x)^2}',
            description: 'Slope formula for least squares regression',
        },
        {
            id: 'sse',
            name: 'Sum of Squared Errors',
            latex: 'SSE = \\sum_{i=1}^n (y_i - \\hat{y}_i)^2',
            description: 'Sum of squared residuals',
        },
    ],
    derivatives: (state, params) => {
        const noise = getParam(params, 'noise');
        const trueSlope = getParam(params, 'trueSlope');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const wrappedX = nextX % 10 - 5;
        // Generate fixed data set
        const seed = 42;
        const data = [];
        for (let i = 0; i < 20; i++) {
            const xi = (i - 10) / 2;
            const yi = trueSlope * xi + 2 + ((Math.sin(seed + i * 1.5) * noise));
            data.push({ x: xi, y: yi });
        }
        // Calculate regression line
        const n = data.length;
        const sumX = data.reduce((a, p) => a + p.x, 0);
        const sumY = data.reduce((a, p) => a + p.y, 0);
        const sumXY = data.reduce((a, p) => a + p.x * p.y, 0);
        const sumXX = data.reduce((a, p) => a + p.x * p.x, 0);
        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;
        const yActual = trueSlope * wrappedX + 2 + (Math.sin(nextX * 3) * noise);
        const yPredicted = slope * wrappedX + intercept;
        const residual = yActual - yPredicted;
        const sse = data.reduce((a, p) => {
            const pred = slope * p.x + intercept;
            return a + (p.y - pred) ** 2;
        }, 0);
        return {
            x: scanRate * 0.016,
            yActual: yActual - getState(state, 'yActual'),
            yPredicted: yPredicted - getState(state, 'yPredicted'),
            residual: residual - getState(state, 'residual'),
            slope: slope - getState(state, 'slope'),
            intercept: intercept - getState(state, 'intercept'),
            sse: sse - getState(state, 'sse'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        yActual: { min: -20, max: 20 },
        yPredicted: { min: -20, max: 20 },
        residual: { min: -10, max: 10 },
    },
});
// ============================================================================
// OUTLIER IMPACT ANALYSIS
// ============================================================================
export const OutlierImpactAnalysis = createExperimentFromSchema({
    id: 'outlier-impact-analysis',
    name: 'Outlier Impact Analysis',
    domain: 'mathematics',
    description: 'Analyze how single outliers affect various statistics',
    conceptTags: ['Outliers', 'Robust Statistics', 'Influence', 'Leverage'],
    difficulty: 4,
    stateVariables: [
        { id: 'outlierValue', name: 'Outlier Value', unit: 'dimensionless', initial: 100, symbol: 'x_{out}' },
        { id: 'mean', name: 'Mean', unit: 'dimensionless', initial: 15.4, symbol: '\\bar{x}' },
        { id: 'median', name: 'Median', unit: 'dimensionless', initial: 5, symbol: '\\tilde{x}' },
        { id: 'mode', name: 'Mode', unit: 'dimensionless', initial: 5, symbol: 'Mode' },
        { id: 'range', name: 'Range', unit: 'dimensionless', initial: 95, symbol: 'R' },
        { id: 'iqr', name: 'IQR', unit: 'dimensionless', initial: 6, symbol: 'IQR' },
    ],
    parameters: [
        { id: 'outlierTarget', name: 'Outlier Target', unit: 'dimensionless', min: -50, max: 200, default: 100, symbol: 'x_{out}' },
        { id: 'rate', name: 'Change Rate', unit: 'dimensionless/s', min: -20, max: 20, default: 5, symbol: 'r' },
    ],
    equations: [
        {
            id: 'robustness',
            name: 'Statistical Robustness',
            latex: '\\text{Robust: Median, IQR} \\quad \\text{Sensitive: Mean, Range}',
            description: 'Some statistics are more affected by outliers than others',
        },
        {
            id: 'outlier-detection',
            name: 'Outlier Detection',
            latex: '|x_i - Q_1| > 1.5 \\cdot IQR \\text{ or } |x_i - Q_3| > 1.5 \\cdot IQR',
            description: 'Common outlier detection using IQR',
        },
    ],
    derivatives: (state, params) => {
        void getParam(params, 'outlierTarget'); // Used for UI
        const rate = getParam(params, 'rate');
        const outlierValue = getState(state, 'outlierValue');
        const nextOutlier = outlierValue + rate * 0.016;
        // Base data: [1, 3, 5, 5, 7, 9]
        const base = [1, 3, 5, 5, 7, 9];
        const data = [...base, nextOutlier].sort((a, b) => a - b);
        const sum = data.reduce((a, b) => a + b, 0);
        const mean = sum / data.length;
        const mid = Math.floor(data.length / 2);
        const median = data.length % 2 === 0
            ? ((data[mid - 1] ?? 0) + (data[mid] ?? 0)) / 2
            : (data[mid] ?? 0);
        // Mode
        const counts = new Map();
        let maxCount = 0;
        let modeValue = data[0] ?? 0;
        for (const val of data) {
            const count = (counts.get(val) || 0) + 1;
            counts.set(val, count);
            if (count > maxCount) {
                maxCount = count;
                modeValue = val;
            }
        }
        const range = (data[data.length - 1] ?? 0) - (data[0] ?? 0);
        const q1Index = Math.floor(data.length / 4);
        const q3Index = Math.floor(3 * data.length / 4);
        const iqr = (data[q3Index] ?? 0) - (data[q1Index] ?? 0);
        return {
            outlierValue: nextOutlier - outlierValue,
            mean: mean - getState(state, 'mean'),
            median: median - getState(state, 'median'),
            mode: modeValue - getState(state, 'mode'),
            range: range - getState(state, 'range'),
            iqr: iqr - getState(state, 'iqr'),
        };
    },
    bounds: {
        outlierValue: { min: -100, max: 300 },
        mean: { min: 0, max: 100 },
        median: { min: 0, max: 20 },
    },
});
// ============================================================================
// SAMPLING DISTRIBUTION SHAPE
// ============================================================================
export const SamplingDistributionShape = createExperimentFromSchema({
    id: 'sampling-distribution-shape',
    name: 'Sampling Distribution Shape',
    domain: 'mathematics',
    description: 'Explore the Central Limit Theorem through sampling',
    conceptTags: ['Central Limit Theorem', 'Sampling Distribution', 'Sample Mean', 'Normal Distribution'],
    difficulty: 5,
    stateVariables: [
        { id: 'sampleSize', name: 'Sample Size n', unit: 'dimensionless', initial: 1, symbol: 'n' },
        { id: 'samples', name: 'Total Samples', unit: 'dimensionless', initial: 1, symbol: 'N' },
        { id: 'sampleMean', name: 'Sample Mean', unit: 'dimensionless', initial: 0.5, symbol: '\\bar{x}' },
        { id: 'stdError', name: 'Std Error σ/√n', unit: 'dimensionless', initial: 0.289, symbol: 'SE' },
        { id: 'meanOfMeans', name: 'Mean of Means', unit: 'dimensionless', initial: 0.5, symbol: 'E[\\bar{X}]' },
        { id: 'stdOfMeans', name: 'Std of Means', unit: 'dimensionless', initial: 0.289, symbol: '\\sigma_{\\bar{X}}' },
    ],
    parameters: [
        { id: 'targetN', name: 'Target Sample Size', unit: 'dimensionless', min: 1, max: 50, default: 5, symbol: 'n' },
        { id: 'populationDist', name: 'Population', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'D' },
        { id: 'samplesPerFrame', name: 'Samples per Frame', unit: 'dimensionless', min: 1, max: 100, default: 10, symbol: 'k' },
        { id: 'seed', name: 'Random Seed', unit: 'dimensionless', min: 1, max: 10000, default: 9999, symbol: 's' },
    ],
    equations: [
        {
            id: 'clt',
            name: 'Central Limit Theorem',
            latex: '\\bar{X}_n \\sim N\\left(\\mu, \\frac{\\sigma^2}{n}\\right)',
            description: 'Sample mean approaches normal distribution as n increases',
        },
        {
            id: 'std-error',
            name: 'Standard Error',
            latex: 'SE = \\frac{\\sigma}{\\sqrt{n}}',
            description: 'Standard deviation of sampling distribution',
        },
    ],
    derivatives: (state, params) => {
        const targetN = Math.round(getParam(params, 'targetN'));
        const populationDist = Math.round(getParam(params, 'populationDist'));
        const samplesPerFrame = Math.round(getParam(params, 'samplesPerFrame'));
        const seed = getParam(params, 'seed');
        const sampleSize = getState(state, 'sampleSize');
        const samples = getState(state, 'samples');
        const nextSampleSize = targetN;
        void (samples + samplesPerFrame); // Next samples value for reference
        // Generate sample from population
        let sampleMean = 0;
        for (let i = 0; i < samplesPerFrame; i++) {
            let sum = 0;
            for (let j = 0; j < nextSampleSize; j++) {
                let val = 0;
                const r = (Math.sin(seed + samples + i * 100 + j) + 1) / 2;
                switch (populationDist) {
                    case 0: // Uniform [0, 1]
                        val = r;
                        break;
                    case 1: // Exponential
                        val = -Math.log(1 - r + 0.001);
                        break;
                    case 2: // Binary (0 or 1)
                        val = r > 0.5 ? 1 : 0;
                        break;
                }
                sum += val;
            }
            sampleMean += sum / nextSampleSize;
        }
        sampleMean /= samplesPerFrame;
        // Population parameters
        let popMean = 0.5;
        let popVar = 1 / 12;
        if (populationDist === 1) {
            popMean = 1;
            popVar = 1;
        }
        else if (populationDist === 2) {
            popMean = 0.5;
            popVar = 0.25;
        }
        const stdError = Math.sqrt(popVar / nextSampleSize);
        // Approximate mean and std of sampling distribution
        const meanOfMeans = popMean;
        const stdOfMeans = stdError;
        return {
            sampleSize: nextSampleSize - sampleSize,
            samples: samplesPerFrame,
            sampleMean: sampleMean - getState(state, 'sampleMean'),
            stdError: stdError - getState(state, 'stdError'),
            meanOfMeans: meanOfMeans - getState(state, 'meanOfMeans'),
            stdOfMeans: stdOfMeans - getState(state, 'stdOfMeans'),
        };
    },
    bounds: {
        sampleSize: { min: 1, max: 50 },
        samples: { min: 1, max: 100000 },
        sampleMean: { min: 0, max: 2 },
    },
});
// ============================================================================
// CONFIDENCE INTERVAL WIDTH
// ============================================================================
export const ConfidenceIntervalWidth = createExperimentFromSchema({
    id: 'confidence-interval-width',
    name: 'Confidence Interval Width',
    domain: 'mathematics',
    description: 'Explore factors affecting confidence interval width',
    conceptTags: ['Confidence Interval', 'Margin of Error', 'Sample Size', 'Confidence Level'],
    difficulty: 4,
    stateVariables: [
        { id: 'n', name: 'Sample Size n', unit: 'dimensionless', initial: 30, symbol: 'n' },
        { id: 'confidence', name: 'Confidence Level', unit: 'dimensionless', initial: 0.95, symbol: '1-\\alpha' },
        { id: 'zScore', name: 'Z-score', unit: 'dimensionless', initial: 1.96, symbol: 'z_{\\alpha/2}' },
        { id: 'margin', name: 'Margin of Error', unit: 'dimensionless', initial: 0.36, symbol: 'E' },
        { id: 'ciLower', name: 'CI Lower', unit: 'dimensionless', initial: 4.64, symbol: 'Lower' },
        { id: 'ciUpper', name: 'CI Upper', unit: 'dimensionless', initial: 5.36, symbol: 'Upper' },
        { id: 'width', name: 'CI Width', unit: 'dimensionless', initial: 0.72, symbol: 'Width' },
    ],
    parameters: [
        { id: 'targetN', name: 'Target Sample Size', unit: 'dimensionless', min: 4, max: 200, default: 30, symbol: 'n' },
        { id: 'targetConfidence', name: 'Target Confidence', unit: 'dimensionless', min: 0.8, max: 0.99, default: 0.95, symbol: 'C' },
        { id: 'popStdev', name: 'Population σ', unit: 'dimensionless', min: 0.5, max: 5, default: 1, symbol: '\\sigma' },
        { id: 'sampleMean', name: 'Sample Mean', unit: 'dimensionless', min: -5, max: 5, default: 5, symbol: '\\bar{x}' },
    ],
    equations: [
        {
            id: 'ci-formula',
            name: 'Confidence Interval',
            latex: '\\bar{x} \\pm z_{\\alpha/2}\\frac{\\sigma}{\\sqrt{n}}',
            description: 'Confidence interval for population mean',
        },
        {
            id: 'margin-error',
            name: 'Margin of Error',
            latex: 'E = z_{\\alpha/2}\\frac{\\sigma}{\\sqrt{n}}',
            description: 'Half-width of confidence interval',
        },
        {
            id: 'sample-size-relation',
            name: 'Sample Size Effect',
            latex: 'n = \\left(\\frac{z_{\\alpha/2}\\sigma}{E}\\right)^2',
            description: 'Required sample size for given margin of error',
        },
    ],
    derivatives: (_state, params) => {
        const targetN = Math.round(getParam(params, 'targetN'));
        const targetConfidence = getParam(params, 'targetConfidence');
        const popStdev = getParam(params, 'popStdev');
        const sampleMean = getParam(params, 'sampleMean');
        // Find z-score for confidence level (approximate)
        void (1 - targetConfidence); // alpha = 1 - confidence
        const zScores = [
            [0.80, 1.28],
            [0.90, 1.645],
            [0.95, 1.96],
            [0.98, 2.33],
            [0.99, 2.576],
        ];
        let zScore = 1.96;
        for (const [conf, z] of zScores) {
            if (targetConfidence >= conf - 0.01) {
                zScore = z;
            }
        }
        const margin = zScore * popStdev / Math.sqrt(targetN);
        const ciLower = sampleMean - margin;
        const ciUpper = sampleMean + margin;
        const width = 2 * margin;
        return {
            n: targetN - getState(_state, 'n'),
            confidence: targetConfidence - getState(_state, 'confidence'),
            zScore: zScore - getState(_state, 'zScore'),
            margin: margin - getState(_state, 'margin'),
            ciLower: ciLower - getState(_state, 'ciLower'),
            ciUpper: ciUpper - getState(_state, 'ciUpper'),
            width: width - getState(_state, 'width'),
        };
    },
    bounds: {
        n: { min: 4, max: 500 },
        margin: { min: 0, max: 5 },
        width: { min: 0, max: 10 },
    },
});
//# sourceMappingURL=statistics.js.map